import AEMHeadless from '@adobe/aem-headless-client-js';

import log from '@/services/logger/log';
import server from '@/services/server/server';

export const S3_VERSION = 'v1';
const VERSION = 'v2';
const AEM_KOTLIN_VERSION = 'v1';
const GRAPHQL_ENDPOINT = 'pwa-app';

let aemClient = null;

const getAemClient = () => {
    if (!aemClient) {
        aemClient = new AEMHeadless({});
    }
    return aemClient;
};

const getAemEnvByReactEnv = (reactEnv) => {
    if (reactEnv === 'dev') return 'stg';

    return reactEnv;
};

const getS3Path = (marketName, relativePath) => {
    return `/content/${GRAPHQL_ENDPOINT}/${marketName}/${S3_VERSION}/${relativePath}`;
};

const getAemResponse = (rawResponse) => {
    const response = rawResponse?.data || {};
    const mainKey = Object.keys(response)[0];

    return response[mainKey]?.item;
};

const initMarketConfigStatic = async (marketName) => {
    try {
        let result;

        if (
            process.env.REACT_APP_ENV === 'dev' ||
            process.env.REACT_APP_ENV === 'poc' ||
            process.env.REACT_APP_ENV === 'stg' ||
            process.env.REACT_APP_ENV === 'pp'
        ) {
            const path = `/content/dam/pwa-app/${marketName}/market-config/${VERSION}/${getAemEnvByReactEnv(process.env.REACT_APP_ENV)}/market-config-static`;

            result = await getAemClient().runPersistedQuery(`${GRAPHQL_ENDPOINT}/get-market-config-static`, {
                path,
            });
        } else {
            const s3Path = getS3Path(marketName, 'configs/staticConfig.json');
            const request = await server.get(s3Path);

            result = request?.data;
        }

        const marketConfig = getAemResponse(result);

        if (!marketConfig) throw new Error('Market config static is not initialized');

        return marketConfig;
    } catch (e) {
        log.error(`initMarketConfigStatic error: ${e}`);
        throw e;
    }
};

const initMarketConfig = async (marketName) => {
    try {
        let result;

        if (
            process.env.REACT_APP_ENV === 'dev' ||
            process.env.REACT_APP_ENV === 'poc' ||
            process.env.REACT_APP_ENV === 'stg' ||
            process.env.REACT_APP_ENV === 'pp'
        ) {
            const path = `/content/dam/pwa-app/${marketName}/market-config/${VERSION}/${getAemEnvByReactEnv(process.env.REACT_APP_ENV)}/market-config`;

            result = await getAemClient().runPersistedQuery(`${GRAPHQL_ENDPOINT}/get-market-config-${VERSION}`, {
                path,
            });
        } else {
            const s3Path = getS3Path(marketName, 'configs/config.json');
            const request = await server.get(s3Path);

            result = request?.data;
        }

        const marketConfig = getAemResponse(result);

        if (!marketConfig) throw new Error('Market config is not initialized');

        return marketConfig;
    } catch (e) {
        log.error(`initMarketConfig error: ${e}`);
        throw e;
    }
};

const initMarketSettings = async (marketName) => {
    try {
        let result;

        if (
            process.env.REACT_APP_ENV === 'dev' ||
            process.env.REACT_APP_ENV === 'poc' ||
            process.env.REACT_APP_ENV === 'stg' ||
            process.env.REACT_APP_ENV === 'pp'
        ) {
            const path = `/content/dam/pwa-app/${AEM_KOTLIN_VERSION}/${marketName}/${getAemEnvByReactEnv(process.env.REACT_APP_ENV)}/global/market-config/market-settings`;

            result = await getAemClient().runPersistedQuery(`${GRAPHQL_ENDPOINT}/get-market-settings`, {
                path,
            });
        } else {
            const s3Path = getS3Path(marketName, 'configs/settings.json');
            const request = await server.get(s3Path);

            result = request?.data;
        }

        const marketConfig = getAemResponse(result);

        if (!marketConfig) throw new Error('Market settings are not initialized');

        return marketConfig;
    } catch (e) {
        log.error(`initMarketSettings error: ${e}`);
        throw e;
    }
};

const initMarketContent = async (marketName, languageCode) => {
    try {
        let result;

        if (
            process.env.REACT_APP_ENV === 'dev' ||
            process.env.REACT_APP_ENV === 'poc' ||
            process.env.REACT_APP_ENV === 'stg' ||
            process.env.REACT_APP_ENV === 'pp'
        ) {
            const path = `/content/dam/pwa-app/${AEM_KOTLIN_VERSION}/${marketName}/${getAemEnvByReactEnv(process.env.REACT_APP_ENV)}/global/${languageCode}/market-content`;

            result = await getAemClient().runPersistedQuery(`${GRAPHQL_ENDPOINT}/get-market-content`, {
                path,
            });
        } else {
            const s3Path = getS3Path(marketName, `${languageCode}/content.json`);
            const request = await server.get(s3Path);

            result = request?.data;
        }

        const marketConfig = getAemResponse(result);

        if (!marketConfig) throw new Error('Market settings are not initialized');

        return marketConfig;
    } catch (e) {
        log.error(`initMarketContent error: ${e}`);
        throw e;
    }
};

const getLocalization = async (marketName, languageCode) => {
    try {
        let result;

        if (
            process.env.REACT_APP_ENV === 'dev' ||
            process.env.REACT_APP_ENV === 'poc' ||
            process.env.REACT_APP_ENV === 'stg' ||
            process.env.REACT_APP_ENV === 'pp'
        ) {
            result = await getAemClient().runPersistedQuery(`${GRAPHQL_ENDPOINT}/get-localization`, {
                path: `/content/dam/pwa-app/${marketName}/${languageCode}/localization`,
            });
        } else {
            const s3Path = getS3Path(marketName, `${languageCode}/localization.json`);
            const request = await server.get(s3Path);

            result = request?.data;
        }

        const localization = getAemResponse(result);

        if (!localization) throw new Error('Localization is not initialized');

        let data = {};

        Object.keys(localization).forEach(function (key, index) {
            data = {...data, ...localization[key]};
        });

        return data;
    } catch (e) {
        log.error(`getLocalization error: ${e}`);
        throw e;
    }
};

export default {
    getLocalization,
    initMarketContent,
    initMarketConfig,
    initMarketSettings,
    initMarketConfigStatic,
};
